import usePermissions from 'components/Navigation/usePermissions'
import {
  showATSStatistics,
  showAssets,
  showAttendanceLeaves,
  showFinancePayroll,
  showWorkforce,
} from 'containers/authorised/Analysis/helper/permission'
import { serverEnvironment } from 'env'
import { Employee } from 'redux/authUser/types'
import i18n from 'translations/i18n'
import { NavDataType } from '@jisr-hr/ds'
import { useSelector } from 'utils/hooks'
import useIsEmployeeOrg from 'hooks/useIsEmployeeOrg'
import { menuList } from './helper'

type ModuleType = {
  module: string
  value: number
}

export type menuType = {
  label: string
  path: string
  icon: string
  testId: string
}

export const MappingNavigation = (): NavDataType => {
  const { employee, company } = useSelector(({ auth }) => auth)
  const payrun_experience_type = useSelector((s) => s.payrun.payrun_experience_type)
  const settingPermissions = usePermissions()
  const isEmployeeOrg = useIsEmployeeOrg()
  const settingNavList = menuList(settingPermissions)
  const { permission_details } = employee!
  const { permissions } = employee as Employee

  const hasModulePermission = (name: string): boolean => {
    const module = permissions.find((f) => f.module === name) as ModuleType
    return module?.value > 0
  }

  const atsPages: menuType[] = []
  if (
    permission_details?.view_jobs_and_its_candidates ||
    permission_details?.manage_applicant_tracking_system
  ) {
    atsPages.push({
      label: i18n.t('jobs'),
      path: '/ats/recruitment/job_openings',
      icon: 'briefcase-02',
      testId: 'jobs',
    })
  }
  if (
    permission_details?.view_jobs_and_its_candidates ||
    permission_details?.view_all_candidates ||
    permission_details?.manage_applicant_tracking_system
  ) {
    atsPages.push({
      label: i18n.t('the_candidates'),
      path: '/ats/recruitment/candidates',
      icon: 'users-01',
      testId: 'candidates',
    })
  }

  const showStatistics = (): boolean => {
    if (
      (settingPermissions.isAnalysis && showFinancePayroll(permission_details)) ||
      showAttendanceLeaves(permission_details) ||
      showWorkforce(permission_details) ||
      showAssets(permission_details)
    ) {
      return true
    }
    return false
  }
  return {
    general: {
      tooltip: i18n.t('home'),
      icon: 'home-02',
      path: '/',
      tab: ['/'],
      testId: 'home',
      event_name: 'Dashboard_Viewed',
    },
    requests: {
      tooltip: i18n.t('requests'),
      icon: 'inbox-01',
      path: '/requests',
      tab: ['requests'],
      testId: 'requests',
      menu: [
        {
          label: i18n.t('your_requests'),
          path: '/requests/employee_requests',
          icon: 'send-01',
          testId: 'your_requests',
          event_name: 'YourRequests_Viewed',
        },
        employee?.can_view_dashboard_request_tabs && {
          label: i18n.t('tasks'),
          path: '/requests/tasks',
          icon: 'check-square',
          testId: 'tasks',
          event_name: 'TasksList_Viewed',
        },
        employee?.can_view_dashboard_request_tabs && {
          label: i18n.t('team_requests'),
          path: '/requests/team_requests',
          icon: 'inbox-01',
          testId: 'team_requests',
          event_name: 'TeamRequests_Viewed',
        },
      ].filter(Boolean),
    },
    ...((hasModulePermission('Employee profile') ||
      hasModulePermission('Muqeem Services') ||
      hasModulePermission('Hired Candidates') ||
      hasModulePermission('Assets')) && {
      employee: {
        tooltip: i18n.t('employees'),
        icon: 'user-01',
        path: '/employees',
        testId: 'employee',
        tab: ['employees', 'organization_chart', 'muqeem_services', 'hired_candidates', 'assets'], // to handle the active state of the tab, because we have different main routes in the same tab
        menu: [
          hasModulePermission('Employee profile') && {
            label: i18n.t('view_employees'),
            path: '/employees',
            icon: 'users-01',
            testId: 'view_employees',
            event_name: 'EmployeeList_Viewed',
          },
          settingPermissions.isMultiSubSupported && {
            label: i18n.t('label.organization_chart'),
            path: '/organization_chart',
            icon: 'dataflow-04',
          },
          hasModulePermission('Muqeem Services') && {
            label: i18n.t('muqeem_services'),
            path: '/muqeem_services',
            icon: 'file-attachment-04',
            nextDivider: true,
            testId: 'muqeem_services',
            event_name: 'ServicesPage_Viewed',
          },
          hasModulePermission('Hired Candidates') && {
            label: i18n.t('hired_candidates'),
            path: '/hired_candidates',
            icon: 'users-plus',
            testId: 'hired_candidates',
          },
          hasModulePermission('Assets') && {
            label: i18n.t('assets'),
            path: '/assets',
            icon: 'laptop-01',
            testId: 'assets',
            event_name: 'AssetsList_Viewed',
          },
        ].filter(Boolean),
      },
    }),
    ...((hasModulePermission('AttendanceDashboard') ||
      hasModulePermission('Leave') ||
      hasModulePermission('Attendance') ||
      hasModulePermission('Scheduler')) && {
      attendance: {
        tooltip: i18n.t('attendance_leaves'),
        icon: 'clock',
        path: '/attendance',
        tab: ['attendance', 'leave_management', 'scheduler', 'dashboard'],
        testId: 'time_attendance',
        menu: [
          hasModulePermission('AttendanceDashboard') && {
            label: i18n.t('attendance_setup'),
            path: '/dashboard',
            icon: 'bar-chart-square-01',
            testId: 'dashboard',
          },
          hasModulePermission('Leave') && {
            label: i18n.t('leave_balance'),
            path: '/leave_management',
            icon: 'percent-03',
            testId: 'leave_balance_tab',
          },
          hasModulePermission('Attendance') && {
            label: i18n.t('attendance'),
            path: '/attendance',
            icon: 'check-square-broken',
            testId: 'attendance',
          },
          hasModulePermission('Scheduler') && {
            label: i18n.t('shift_scheduler'),
            path: '/scheduler',
            icon: 'calendar',
            testId: 'shift_scheduler',
          },
        ].filter(Boolean),
      },
    }),
    ...((hasModulePermission('Payroll') ||
      hasModulePermission('FinalSettlement') ||
      hasModulePermission('VacationSettlement') ||
      hasModulePermission('PaymentProcessing')) && {
      payroll: {
        tooltip: i18n.t('payroll'),
        icon: 'coins-stacked-01',
        path: '/payroll',
        testId: 'payroll',
        tab: ['payroll', 'off_cycle', 'payment_processing', 'gosi_sync'],
        menu: [
          hasModulePermission('Payroll') && {
            label: i18n.t('payroll'),
            path: '/payroll',
            icon: 'table',
            testId: 'payroll_table',
          },
          hasModulePermission('Payroll') &&
            payrun_experience_type === 'new' && {
              label: i18n.t('gosi_sync'),
              path: '/gosi_sync',
              icon: 'refresh-ccw-02',
              testId: 'gosi_sync',
              event_name: 'gosi_screen_gosi_web',
            },
          hasModulePermission('VacationSettlement') && {
            label: i18n.t('vacation_settlement'),
            path: '/off_cycle/vacation_settlement',
            icon: 'sun-setting-01',
            testId: 'vacation_settlement',
          },
          hasModulePermission('FinalSettlement') && {
            label: i18n.t('final_settlement'),
            path: '/off_cycle/final_settlement',
            icon: 'log-out-03',
            testId: 'final_settlement',
          },
          hasModulePermission('PaymentProcessing') &&
            payrun_experience_type === 'old' && {
              label: i18n.t('payment_processing'),
              path: '/payment_processing',
              icon: 'calendar',
              testId: 'payment_processing',
            },
        ].filter(Boolean),
      },
    }),
    ...((hasModulePermission('performanceCyclesCompetencies') ||
      hasModulePermission('PerformanceObjectives')) &&
      isEmployeeOrg && {
        performance: {
          tooltip: i18n.t('performance'),
          icon: 'rocket-01',
          path: '/performance',
          tab: ['performance'],
          testId: 'performance',
          menu: [
            hasModulePermission('performanceCyclesCompetencies') && {
              label: i18n.t('performance_cycles'),
              path: '/performance/cycles',
              icon: 'intersect-circle',
              testId: 'performance_cycles',
            },
            hasModulePermission('PerformanceObjectives') && {
              label: i18n.t('objectives'),
              path: '/performance/objectives',
              icon: 'target-04',
              testId: 'objectives',
            },
            hasModulePermission('performanceCyclesCompetencies') && {
              label: i18n.t('competencies'),
              path: '/performance/competencies',
              icon: 'check-heart',
              testId: 'competencies',
            },
          ].filter(Boolean),
        },
      }),
    ...(hasModulePermission('ATSRecruitment') &&
      atsPages?.length > 0 && {
        ats: {
          tooltip: i18n.t('applicant_tracking'),
          icon: 'briefcase-02',
          path: '/ats',
          tab: ['ats'],
          testId: 'ats',
          menu: atsPages,
        },
      }),
    ...((hasModulePermission('Reports') || showStatistics()) && {
      statistics: {
        tooltip: i18n.t('reports_statistics'),
        icon: 'bar-chart-01',
        tab: ['data_analysis', 'reports'],
        dot: false,
        testId: 'reports',
        menu: [
          settingPermissions.isAnalysis && {
            group: {
              title: i18n.t('statistics'),
              menu: [
                showFinancePayroll(permission_details) && {
                  groupTitle: i18n.t('statistics'),
                  label: i18n.t('payroll_statistics'),
                  path: '/data_analysis/finance_payroll',
                  icon: 'coins-stacked-01',
                  testId: 'payroll_statistics',
                },
                showAttendanceLeaves(permission_details) && {
                  label: i18n.t('attendance_statistics'),
                  path: '/data_analysis/attendance_leaves',
                  icon: 'clock',
                  testId: 'attendance_statistics',
                },
                showWorkforce(permission_details) && {
                  label: i18n.t('employees_statistics'),
                  path: '/data_analysis/workforce',
                  icon: 'calendar',
                  testId: 'employees_statistics',
                },
                showAssets(permission_details) && {
                  label: i18n.t('assets_statistics'),
                  path: '/data_analysis/assets',
                  icon: 'hard-drive',
                  testId: 'assets_statistics',
                },
                showATSStatistics(permission_details, company?.subscribed_features ?? []) && {
                  label: i18n.t('ats_statistics'),
                  path: '/data_analysis/ats_statistics',
                  icon: 'briefcase-02',
                  testId: 'ats_statistics',
                },
              ].filter(Boolean),
            },
          },
          hasModulePermission('Reports') && {
            group: {
              title: i18n.t('reports'),
              hideDivider: true,
              menu: [
                {
                  label: i18n.t('All_reports'),
                  path: '/reports',
                  icon: 'file-07',
                  testId: 'all_reports',
                },
              ],
            },
          },
        ].filter(Boolean),
      },
    }),
    ...(hasModulePermission('BenefitsManagement') && {
      benefitsMamangement: {
        tooltip: i18n.t('benefits_management'),
        icon: 'heart-hand',
        tab: ['benefits_management'],
        path: '/benefits_management',
        dot: false,
        testId: 'benefits_management',
        menu: [
          {
            group: {
              title: i18n.t('benefits_management_health_insurance'),
              hideDivider: true,
              menu: [
                {
                  label: i18n.t('benefits_management_providers'),
                  path: '/benefits_management/insurance/providers',
                  testId: 'providers',
                },
                {
                  label: i18n.t('benefits_management_policies'),
                  path: '/benefits_management/insurance/policies',
                  testId: 'policies',
                },
                {
                  label: i18n.t('endorsements'),
                  path: '/benefits_management/insurance/endorsements',
                  testId: 'endorsements',
                },
              ],
            },
          },
        ],
      },
    }),
    ...(['aws', 'staging', 'oci', 'dev'].includes(serverEnvironment) &&
      settingPermissions?.manage_integrations && {
        marketplace: {
          tooltip: i18n.t('market_place'),
          icon: 'building-02',
          label: i18n.t('apps'),
          dot: false,
          path: `/marketplace`,
          tab: ['marketplace'],
          testId: 'integration_apps',
        },
      }),
    ...(hasModulePermission('Automations') &&
      settingPermissions?.isAutomationSupported && {
        automations: {
          tooltip: i18n.t('automation'),
          icon: 'lightning-01',
          path: '/automations',
          tab: ['automations'],
          testId: 'automations',
          menu: [
            {
              label: i18n.t('automation'),
              path: '/automations/list',
              icon: 'zap-fast',
              testId: 'automations_list',
              event_name: 'Automations_Viewed',
            },
            {
              label: i18n.t('templates'),
              path: '/automations/templates',
              icon: 'zap-square',
              testId: 'templates',
            },
            {
              label: i18n.t('history_automation'),
              path: '/automations/history',
              icon: 'clock',
              testId: 'history_automation',
              event_name: 'AutomationsHistory_Viewed',
            },
          ],
        },
      }),
    ...(hasModulePermission('Settings') && {
      settings: {
        tooltip: i18n.t('settings'),
        icon: 'settings-02',
        dot: false,
        path: '/setting_v2',
        tab: ['setting_v2'],
        testId: 'settings',
        menu: settingNavList,
      },
    }),
  } as NavDataType
}
